
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { userService } from "./userService";

interface InviteDetails {
  inviteId: string;
  displayName: string;
  phoneNumber?: string;
  eventId: string;
  rsvpStatus?: string;
  claimed: boolean;
  claimedByUserId?: string;
  selectedDates?: string[];
  selectedTimes?: string[];
  feedbackText?: string;
}

/**
 * Fetches invite details by invite ID
 * @param inviteId The invite ID to lookup
 * @returns Invite details or null if not found
 */
export const getInviteById = async (inviteId: string): Promise<InviteDetails | null> => {
  try {
    const { data, error } = await supabase
      .from('event_invites')
      .select('*')
      .eq('invite_id', inviteId)
      .maybeSingle();
    
    if (error) throw error;
    if (!data) return null;
    
    return {
      inviteId: data.invite_id,
      displayName: data.display_name,
      phoneNumber: data.phone_number,
      eventId: data.event_id,
      rsvpStatus: data.rsvp_status,
      claimed: data.claimed,
      claimedByUserId: data.claimed_by_user_id,
      selectedDates: data.selected_dates,
      selectedTimes: data.selected_times,
      feedbackText: data.feedback_text
    };
  } catch (error) {
    console.error("Error fetching invite:", error);
    return null;
  }
};

/**
 * Gets the phone number associated with an invite
 * @param inviteId The invite ID to lookup
 * @returns The phone number or null if not found
 */
export const getInvitePhoneNumber = async (inviteId: string): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .from('event_invites')
      .select('phone_number, claimed_by_user_id')
      .eq('invite_id', inviteId)
      .single();
    
    if (error) throw error;
    
    // If invite is claimed by a user, get the user's phone number
    if (data.claimed_by_user_id) {
      return await userService.getUserPhoneNumber(data.claimed_by_user_id);
    }
    
    // Otherwise return the invite's phone number
    return data.phone_number;
  } catch (error) {
    console.error("Error getting invite phone number:", error);
    return null;
  }
};

/**
 * Updates the temporary claim on an invite for the feedback form
 * @param inviteId The invite ID to temporarily claim
 * @param sessionId The session ID claiming the invite
 * @returns Success status of the operation
 */
export const temporaryClaimInvite = async (inviteId: string, sessionId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('event_invites')
      .update({
        feedback_started: new Date().toISOString(),
        feedback_started_by: sessionId
      })
      .eq('invite_id', inviteId)
      .is('feedback_started', null);
    
    if (error) {
      console.error("Error claiming invite:", error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error("Error temporarily claiming invite:", error);
    return false;
  }
};

/**
 * Clears the temporary claim on an invite
 * @param inviteId The invite ID to release
 * @param sessionId The session ID that claimed the invite
 * @returns Success status of the operation
 */
export const clearTemporaryInviteClaim = async (inviteId: string, sessionId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('event_invites')
      .update({
        feedback_started: null,
        feedback_started_by: null
      })
      .eq('invite_id', inviteId)
      .eq('feedback_started_by', sessionId);
    
    if (error) {
      console.error("Error clearing invite claim:", error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error("Error clearing temporary invite claim:", error);
    return false;
  }
};

/**
 * Gets available invites for an event (not claimed and not archived)
 * @param eventId The event ID to look up invites for
 * @returns Array of available invites
 */
export const getAvailableInvites = async (eventId: string): Promise<InviteDetails[]> => {
  try {
    const { data, error } = await supabase
      .from('event_invites')
      .select('*')
      .eq('event_id', eventId)
      .eq('archived', false)
      .is('claimed', false);
    
    if (error) throw error;
    
    return data.map(invite => ({
      inviteId: invite.invite_id,
      displayName: invite.display_name,
      phoneNumber: invite.phone_number,
      eventId: invite.event_id,
      rsvpStatus: invite.rsvp_status,
      claimed: invite.claimed,
      claimedByUserId: invite.claimed_by_user_id,
      selectedDates: invite.selected_dates,
      selectedTimes: invite.selected_times,
      feedbackText: invite.feedback_text
    }));
  } catch (error) {
    console.error("Error fetching available invites:", error);
    return [];
  }
};

/**
 * Updates an invite with feedback and RSVP response
 * @param inviteId The invite ID to update
 * @param feedback The feedback data to update
 * @returns Success status of the operation
 */
export const submitInviteFeedback = async (
  inviteId: string,
  feedback: {
    rsvpStatus: string;
    selectedDates?: string[];
    selectedTimes?: string[];
    feedbackText?: string;
    phoneNumber?: string;
    userId?: string;
    guestAvatarUrl?: string;
  }
): Promise<boolean> => {
  try {
    const updateData: Record<string, any> = {
      claimed: true,
      claimed_at: new Date().toISOString(),
      rsvp_status: feedback.rsvpStatus,
      responded_at: new Date().toISOString(),
      feedback_started: null,
      feedback_started_by: null
    };
    
    // Always save phone number with the invite if provided
    if (feedback.phoneNumber) {
      updateData.phone_number = feedback.phoneNumber;
    }
    
    // If user is associated, update user ID
    if (feedback.userId) {
      updateData.claimed_by_user_id = feedback.userId;
    }
    
    if (feedback.selectedDates) updateData.selected_dates = feedback.selectedDates;
    if (feedback.selectedTimes) updateData.selected_times = feedback.selectedTimes;
    
    // Always include feedback_text in the update data
    // Use null for empty strings or undefined values to match database expectations
    updateData.feedback_text = feedback.feedbackText !== undefined && feedback.feedbackText !== '' ? 
      feedback.feedbackText : null;
    
    // Add guest avatar URL to update data if provided
    if (feedback.guestAvatarUrl) {
      updateData.guest_avatar_url = feedback.guestAvatarUrl;
    }
    
    console.log('Updating invite with data:', updateData);
    console.log('Original feedback text value:', feedback.feedbackText);
    console.log('Processed feedback_text value for database:', updateData.feedback_text);
    
    const { error, data } = await supabase
      .from('event_invites')
      .update(updateData)
      .eq('invite_id', inviteId)
      .select('feedback_text, guest_avatar_url');
    
    if (error) {
      console.error("Error submitting invite feedback:", error);
      return false;
    }
    
    console.log('Database update result:', data);
    return true;
  } catch (error) {
    console.error("Error submitting invite feedback:", error);
    return false;
  }
};

/**
 * Links an invite to a user
 * @param inviteId The invite ID to link
 * @param userId The user ID to link the invite to
 * @param phoneNumber The phone number to associate with the invite
 * @returns Success status of the operation
 */
export const linkInviteToUser = async (
  inviteId: string, 
  userId: string, 
  phoneNumber?: string
): Promise<boolean> => {
  try {
    const updateData: Record<string, any> = {
      claimed: true,
      claimed_at: new Date().toISOString(),
      claimed_by_user_id: userId,
      // Clear phone_number from invite as we'll use the user's phone from the users table
      phone_number: null
    };
    
    const { error } = await supabase
      .from('event_invites')
      .update(updateData)
      .eq('invite_id', inviteId);
    
    if (error) {
      console.error("Error linking invite to user:", error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error("Error linking invite to user:", error);
    return false;
  }
};

/**
 * Creates a new invite for an event
 * @param eventId The event ID to create an invite for
 * @param displayName The display name for the invite
 * @param phoneNumber Optional phone number for the invite
 * @returns The created invite ID or null if creation failed
 */
export const createInvite = async (
  eventId: string,
  displayName: string,
  phoneNumber?: string
): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .from('event_invites')
      .insert({
        event_id: eventId,
        display_name: displayName,
        phone_number: phoneNumber,
        rsvp_status: 'invited'
      })
      .select('invite_id')
      .single();
    
    if (error) {
      console.error("Error creating invite:", error);
      return null;
    }
    
    return data.invite_id;
  } catch (error) {
    console.error("Error creating invite:", error);
    return null;
  }
};

/**
 * Sends an SMS notification for an event
 * @param eventId The event ID to send a notification for
 * @param inviteId The invite ID to send a notification to
 * @param notificationType The type of notification to send
 * @param phoneNumber Optional direct phone number to use instead of looking it up
 * @returns Success status of the operation
 */
export const sendEventNotification = async (
  eventId: string,
  inviteId: string,
  notificationType: string,
  phoneNumber?: string
): Promise<boolean> => {
  try {
    if (!eventId || !inviteId) {
      console.error('Missing required parameters for sending notification:', { eventId, inviteId });
      return false;
    }

    console.log('sendEventNotification called with params:', { 
      eventId, 
      inviteId, 
      notificationType, 
      phoneNumber: phoneNumber ? 'provided' : 'not provided' 
    });
    
    // Use provided phone number or look it up if not provided
    let recipientPhone = phoneNumber;
    
    if (!recipientPhone) {
      recipientPhone = await getInvitePhoneNumber(inviteId);
      
      if (!recipientPhone) {
        console.error('No phone number found for invite:', inviteId);
        return false;
      }
    }
    
    console.log(`Sending ${notificationType} notification to ${recipientPhone} for invite ${inviteId}`);
    
    // Check if notification has already been sent
    const { data: existingNotification } = await supabase
      .from('event_notifications')
      .select('*')
      .eq('event_id', eventId)
      .eq('invite_id', inviteId)
      .eq('notification_type', notificationType)
      .eq('status', 'sent')
      .maybeSingle();
    
    if (existingNotification) {
      console.log('Notification already sent');
      return true;
    }
    
    // Invoke the Edge Function
    const { data, error } = await supabase.functions.invoke('send-sms', {
      body: {
        eventId,
        inviteId,
        notificationType,
        phone: recipientPhone
      }
    });
    
    if (error) {
      console.error('Error from Edge Function:', error);
      return false;
    }
    
    console.log('SMS function response:', data);
    return data?.success || false;
  } catch (error) {
    console.error('Error sending event notification:', error);
    return false;
  }
};

export const inviteService = {
  getInviteById,
  getInvitePhoneNumber,
  temporaryClaimInvite,
  clearTemporaryInviteClaim,
  getAvailableInvites,
  submitInviteFeedback,
  linkInviteToUser,
  createInvite,
  sendEventNotification
};
